import { useEffect } from "react";

const SITE = (process.env.REACT_APP_SITE_URL || "https://eleganthomeadvisors.in").replace(/\/+$/, "");
// Shown when a page has no picture of its own (built by scripts/prerender.mjs from the home page photo).
export const DEFAULT_SHARE_IMAGE = `${SITE}/og-image.jpg`;

function upsertMeta(attr, name, content, created) {
    if (!content) return;
    let el = document.head.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
        created.push(el);
    } else if (!el.dataset.seoPrev) {
        el.dataset.seoPrev = el.getAttribute("content") || "";
    }
    el.setAttribute("content", content);
}

/**
 * Sets the page title, description, canonical URL, Open Graph / Twitter tags and JSON-LD.
 * Everything it adds is removed or restored when the page unmounts.
 */
export default function Seo({ title, description, image, path, type = "website", jsonLd, noindex = false }) {
    const ld = jsonLd ? JSON.stringify(jsonLd) : "";
    useEffect(() => {
        // The page was opened from pre-built HTML (scripts/prerender.mjs): its tags are replaced by the live ones.
        document.head.querySelectorAll("[data-pr]").forEach((el) => el.remove());
        const created = [];
        const prevTitle = document.title;
        if (title) document.title = title;
        const url = path ? `${SITE}${path}` : window.location.href;

        upsertMeta("name", "description", description, created);
        upsertMeta("property", "og:title", title, created);
        upsertMeta("property", "og:description", description, created);
        upsertMeta("property", "og:type", type, created);
        upsertMeta("property", "og:url", url, created);
        const shareImage = image || DEFAULT_SHARE_IMAGE;
        upsertMeta("property", "og:image", shareImage, created);
        upsertMeta("property", "og:site_name", "Elegant Home Advisors", created);
        upsertMeta("name", "twitter:card", "summary_large_image", created);
        upsertMeta("name", "twitter:title", title, created);
        upsertMeta("name", "twitter:description", description, created);
        upsertMeta("name", "twitter:image", shareImage, created);
        upsertMeta("name", "robots", noindex ? "noindex, follow" : "index, follow, max-image-preview:large", created);

        let canonical = document.head.querySelector('link[rel="canonical"]');
        const canonicalCreated = !canonical;
        const prevHref = canonical?.getAttribute("href");
        if (!canonical) {
            canonical = document.createElement("link");
            canonical.setAttribute("rel", "canonical");
            document.head.appendChild(canonical);
        }
        canonical.setAttribute("href", url);
        if (noindex) canonical.remove();   // pages kept out of search (404, shortlist) have no canonical

        let script;
        if (ld) {
            script = document.createElement("script");
            script.type = "application/ld+json";
            script.dataset.seo = "1";
            script.text = ld;
            document.head.appendChild(script);
        }

        return () => {
            document.title = prevTitle;
            created.forEach((el) => el.remove());
            document.head.querySelectorAll("meta[data-seo-prev]").forEach((el) => {
                el.setAttribute("content", el.dataset.seoPrev);
                delete el.dataset.seoPrev;
            });
            if (canonicalCreated) canonical.remove();
            else if (prevHref) canonical.setAttribute("href", prevHref);
            if (script) script.remove();
        };
    }, [title, description, image, path, type, ld, noindex]);
    return null;
}

export { SITE };
